import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { useParams, useNavigate } from "react-router-dom";
import portalsApi from "../api/portalsApi";
import articleApi from "../api/articleApi";
import { stripHtmlTags } from "../utils/stripHtmlTags";
import Spinner from "../components/spinner";
import ImageWithFallback from "../helpers/ImageWithFallback";
import PortalLogoFallback from "../helpers/PortalLogoFallback";
import adsApi from "../api/adsApi";
import { IoArrowBack, IoOpenOutline } from "react-icons/io5";

// Lazy load components
const RelatedArticles = lazy(() => import("../components/relatedArticles"));
const CommentSection = lazy(() => import("../components/commentSection"));
const LikeButton = lazy(() => import("../components/likeButton"));

// Loading skeleton components
const RelatedArticlesSkeleton = () => (
  <div className="bg-white dark:bg-gray-900 w-full lg:w-[30%] p-4 rounded-lg shadow-md animate-pulse">
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex space-x-3">
          <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CommentSectionSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const LikeButtonSkeleton = () => (
  <div className="flex items-center space-x-2 animate-pulse">
    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
    <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
  </div>
);

const AdComponent = ({ ad }) => {
  if (!ad || !ad.is_active) {
    return (
      <div className="h-[90px] w-full bg-gray-100 border border-gray-200 rounded flex items-center justify-center">
        <span className="text-gray-500 font-medium">Advertisement</span>
      </div>
    );
  }

  return (
    <div className="h-[90px] w-full bg-white rounded overflow-hidden flex items-center justify-center">
      <a 
        href={ad.link} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="block h-full"
      >
        <img 
          src={ad.image} 
          alt={ad.title} 
          className="h-full w-auto object-contain"
        />
      </a>
    </div>
  );
};

const SingleArticlePage = ({ onNotFound }) => {
  const navigate = useNavigate();
  const { id: articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portals, setPortals] = useState([]);
  const [ads, setAds] = useState({});
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const storedCategoryId = decodeURIComponent(
    localStorage.getItem("categoryId") || "Unknown Categorization"
  );
  const articleRef = useRef(null);

  // Scroll to the top when the page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle scroll restoration for browser back navigation
  useEffect(() => {
    const handlePopState = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Listen for browser back/forward navigation
    window.addEventListener('popstate', handlePopState);
    
    // Also handle when page loads after back navigation
    if (window.performance && window.performance.navigation.type === window.performance.navigation.TYPE_BACK_FORWARD) {
      window.scrollTo(0, 0);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Scroll to the article section
  useEffect(() => {
    if (articleRef.current) {
      const yOffset = -80; // Adjust the value to control scroll position
      const element = articleRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [article]);

  // Fetch the main article by ID
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setIframeLoaded(false); // Reset iframe loading state
        const response = await articleApi.getById(articleId);
        setArticle(response.data);
      } catch (err) {
        if (err.response?.status === 404 && onNotFound) {
          onNotFound();
        } else {
          setError("Failed to load the article. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [articleId, onNotFound]);

  // Fetch portals
  useEffect(() => {
    const fetchPortals = async () => {
      try {
        const { data } = await portalsApi.getAll();
        setPortals(data);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      }
    };

    fetchPortals();
  }, []);

  // Fetch ads
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await adsApi.getAll();
        if (response.data?.status === 'success') {
          const processedAds = {};
          Object.entries(response.data.data).forEach(([key, adList]) => {
            processedAds[key] = adList.map(ad => ({
              ...ad,
              image: ad.image.startsWith('http') ? ad.image : `${process.env.REACT_APP_API_URL}${ad.image}`
            }));
          });
          setAds(processedAds);
        }
      } catch (error) {
        console.error("Error fetching ads:", error);
      }
    };
    fetchAds();
  }, []);

  // Function to handle portal logo click and redirect
  const handlePortalClick = (portalId) => {
    const portal = portals.find((p) => p.id === portalId);
    if (portal) {
      const sanitizedPortalName = stripHtmlTags(portal.name)
        .replace(/%20/g, "-")
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9-]/g, "")
        .toLowerCase()
        .trim();

      localStorage.setItem("portalName", sanitizedPortalName);
      localStorage.setItem("portalId", portal.id);

      navigate(`/portal/${sanitizedPortalName}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getPortalLogo = (portalId) => {
    if (!portals || portals.length === 0) return "";
    return portals.find((portal) => portal.id === portalId)?.logo_url || "";
  };

  if (isLoading) return <Spinner />;
  if (error) return <div>Error: {error}</div>;
  if (!article) return <div>No article found.</div>;

  const portalLogo = getPortalLogo(article.portal);
  const portalName = portals.find((portal) => portal.id === article.portal)?.name || "";

  return (
    <div className="container mx-auto p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl border-b-2 border-gray-800 dark:border-gray-300 pb-2 font-bold text-gray-900 dark:text-white">
          
        </h1>
        <button
          onClick={() => {
            navigate(-1);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
            bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg 
            hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-customOrange dark:hover:text-customOrange 
            focus:outline-none focus:ring-2 focus:ring-customOrange focus:ring-offset-2 
            transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 
            group"
        >
          <IoArrowBack className="w-4 h-4 mr-2 transition-transform duration-200 group-hover:-translate-x-1" />
          <span>Kthehu mbrapa</span>
        </button>
      </div>
      <div className="w-full h-0.5 mb-10 bg-gray-200 dark:bg-gray-700"></div>
      
      <div className="flex flex-col lg:flex-row" ref={articleRef}>
        <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 p-4 shadow-md w-full lg:w-[70%] mb-4 lg:mb-0 lg:mr-4">
          {portalLogo && (
            <div
              className="flex justify-start mb-6 w-20 h-10"
              onClick={() => handlePortalClick(article.portal)}
              role="button"
            >
              <ImageWithFallback
                src={portalLogo}
                className="w-10 h-10 object-contain rounded-full border border-gray-300 dark:border-gray-600 shadow-lg cursor-pointer"
                fallback={<PortalLogoFallback portalName={portalName} isOverlay={false} />}
              />
            </div>
          )}
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">
            {article.title}
          </h1>

          <AdComponent ad={ads?.article_content_top?.[0]} />

          <div className="relative">
            {!iframeLoaded && (
              <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading article content...</p>
                </div>
              </div>
            )}
            <iframe
              src={article.url}
              title={article.title}
              className={`w-full h-[800px] lg:h-[1400px] border-none rounded-lg transition-opacity duration-300 ${
                iframeLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={() => setIframeLoaded(true)}
            ></iframe>
            {/* External link button */}
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 right-4 z-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full lg:rounded-full p-2 lg:pr-4 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center lg:space-x-2"
              title="Open original article in new tab"
              aria-label="Open original article in new tab"
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            >
              <IoOpenOutline className="w-6 h-6 text-gray-700 dark:text-gray-200" />
              <span className="hidden lg:inline text-sm font-medium text-gray-700 dark:text-gray-200 ml-2 whitespace-nowrap">
                Lexo artikullin në faqen origjinale
              </span>
            </a>
          </div>
          
          <Suspense fallback={<LikeButtonSkeleton />}>
            <LikeButton
              articleId={article.id}
              initialLikeStatus={article.liked}
              likesCount={article.likes_count}
            />
          </Suspense>
        </div>

        <Suspense fallback={<RelatedArticlesSkeleton />}>
          <RelatedArticles 
            key={articleId} 
            articles={article.similar_articles || []} 
            portals={portals} 
          />
        </Suspense>
      </div>

      <div className="mt-8 flex flex-col lg:flex-row justify-between">
        <div className="bg-white dark:bg-gray-900 w-full lg:w-[70%] lg:mb-0 lg:mr-4 p-4 rounded-lg shadow-md">
          <AdComponent ad={ads?.article_comments_top?.[0]} />
          <Suspense fallback={<CommentSectionSkeleton />}>
            <CommentSection article={article} />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default SingleArticlePage;
